import React, { useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ComunidadTab({ userData, refreshData }) {
  const { getToken } = useAdminAuth();
  const { user, community, enrollment } = userData;
  const [isProcessing, setIsProcessing] = useState(false);

  const handleGrantAccess = async () => {
    if (!window.confirm('¿Otorgar acceso a la comunidad?')) return;
    
    setIsProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/community/grant`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Acceso a comunidad otorgado');
      refreshData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al otorgar acceso');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRevokeAccess = async () => {
    if (!window.confirm('¿Revocar acceso a la comunidad? El usuario ya no podrá acceder.')) return;
    
    setIsProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/community/revoke`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Acceso revocado correctamente');
      refreshData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al revocar acceso');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResendLink = async () => {
    setIsProcessing(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/community/resend-link`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Link reenviado por email');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al reenviar link');
    } finally {
      setIsProcessing(false);
    }
  };

  const copyJoinLink = () => {
    if (community?.join_url) {
      navigator.clipboard.writeText(community.join_url);
      alert('Link copiado al portapapeles');
    } else {
      alert('No hay link disponible. Usuario debe estar activo.');
    }
  };

  const getStatusColor = (status) => {
    return {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      revoked: 'bg-red-500/20 text-red-400 border-red-500/30',
    }[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getStatusIcon = (status) => {
    return {
      active: '✅',
      pending: '⏳',
      revoked: '❌',
    }[status] || '❓';
  };

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className={`rounded-lg p-6 border ${
        community?.status === 'active' ? 'bg-green-500/10 border-green-500/30' :
        community?.status === 'pending' ? 'bg-yellow-500/10 border-yellow-500/30' :
        'bg-red-500/10 border-red-500/30'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{getStatusIcon(community?.status)}</span>
            <div>
              <h3 className="text-2xl font-bold text-white">Estado de Comunidad</h3>
              <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(community?.status)}`}>
                {community?.status?.toUpperCase() || 'SIN ACCESO'}
              </span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            {community?.status === 'active' ? (
              <>
                <button
                  onClick={handleResendLink}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors border border-blue-500/50 disabled:opacity-50"
                >
                  📧 Reenviar Link
                </button>
                <button
                  onClick={handleRevokeAccess}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors border border-red-500/50 disabled:opacity-50"
                >
                  ❌ Revocar Acceso
                </button>
              </>
            ) : enrollment?.status === 'active' ? (
              <button
                onClick={handleGrantAccess}
                disabled={isProcessing}
                className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors border border-green-500/50 disabled:opacity-50"
              >
                ✅ Otorgar Acceso
              </button>
            ) : (
              <div className="text-sm text-gray-400 italic">
                Usuario debe estar activo primero
              </div>
            )}
          </div>
        </div>

        {/* Status Description */}
        <div className="text-sm text-gray-300">
          {community?.status === 'active' && (
            <p>✅ El usuario tiene acceso activo a la comunidad y puede participar en todos los canales.</p>
          )}
          {community?.status === 'pending' && (
            <p>⏳ El acceso está pendiente. El usuario recibirá el link de invitación por email.</p>
          )}
          {community?.status === 'revoked' && (
            <p>❌ El acceso ha sido revocado. El usuario no puede acceder a la comunidad.</p>
          )}
          {!community?.status && (
            <p>El usuario aún no tiene acceso a la comunidad.</p>
          )}
        </div>
      </div>

      {/* Groups/Channels */}
      {community?.groups && community.groups.length > 0 && (
        <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
          <h4 className="font-semibold text-white mb-4">Grupos Asignados</h4>
          <div className="space-y-3">
            {community.groups.map((group) => (
              <div key={group.id} className="flex items-center justify-between p-4 bg-black/50 rounded-lg border border-[#1a4d5c]/20">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">👥</span>
                  <div>
                    <div className="text-white font-medium">{group.name}</div>
                    {group.description && (
                      <div className="text-gray-400 text-sm">{group.description}</div>
                    )}
                  </div>
                </div>
                {group.member_count && (
                  <div className="text-gray-400 text-sm">
                    {group.member_count} miembros
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Join Link */}
      {community?.join_url && community.status === 'active' && (
        <div className="bg-[#D4AF37]/10 rounded-lg p-6 border border-[#D4AF37]/30">
          <h4 className="font-semibold text-[#D4AF37] mb-3">🔗 Link de Acceso</h4>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={community.join_url}
              readOnly
              className="flex-1 px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white font-mono text-sm"
            />
            <button
              onClick={copyJoinLink}
              className="px-4 py-2 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] rounded-lg transition-colors"
            >
              📋 Copiar
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Este link permite al usuario unirse a la comunidad privada
          </p>
        </div>
      )}

      {/* Activity/Engagement Stats (Optional - if available) */}
      {community?.last_activity_at && (
        <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
          <h4 className="font-semibold text-white mb-4">Actividad en Comunidad</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-gray-400 text-sm">Última Actividad</div>
              <div className="text-white font-semibold">
                {new Date(community.last_activity_at).toLocaleDateString('es-ES')}
              </div>
            </div>
            {community.messages_count !== undefined && (
              <div>
                <div className="text-gray-400 text-sm">Mensajes Enviados</div>
                <div className="text-white font-semibold">{community.messages_count}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Warning for inactive users */}
      {enrollment?.status !== 'active' && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <h4 className="text-yellow-400 font-semibold mb-1">⚠️ Usuario Inactivo</h4>
          <p className="text-sm text-yellow-300">
            El usuario debe tener su cuenta activada antes de poder acceder a la comunidad.
          </p>
        </div>
      )}
    </div>
  );
}